import { defineStore, storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { format } from 'date-fns';
import { isEmpty, kebabCase } from 'lodash-es';
import { useAppLoading } from '@/stores/app/loading';
import { useAppAuthData } from '@/stores/app/authData';
import {
  getEmployeeAcknowledgements,
  getEmployeeAcknowledgementSummary,
  getEmployeeAcknowledgementPreview,
  createEmployeeAcknowledgement,
  cancelEmployeeAcknowledgement,
  sendEmployeeAcknowledgementReminder,
  downloadEmployeeAcknowledgementsCsv as downloadEmployeeAcknowledgementsCsvRequest,
} from '@/api/jobProfile';
import { handleCSVDownload } from '@/helpers/fileDownload';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useSubMenu } from '@/stores/filters/subMenu';
import { useAppPagination } from '@/stores/app/pagination';
import { useFilters } from '@/stores/filters/filters';
import { useModals } from '@/stores/modals/modals';
import { normalizeCreateModalPayload } from '@/stores/jobProfiles/helpers';
import { READABLE_FILE_NAME_FORMAT } from '@/helpers/date';
import { ACKNOWLEDGEMENT_ALERT_LABELS } from '@/helpers/acknowledgementAlerts';

export const useEmployeeAcknowledgementsStore = defineStore('employeeAcknowledgements', () => {
  const { setLoading } = useAppLoading();
  const { updateFilters, sendFilterRequest, loadFilters } = useFilters();
  const { addSuccessSnackbar, addErrorSnackbar } = useAppSnackbar();
  const { setModal } = useModals();

  const { selectedSubMenuOption } = storeToRefs(useSubMenu());
  const { fields, mergedTempFilters } = storeToRefs(useFilters());
  const { authData } = storeToRefs(useAppAuthData());

  const employeeAcknowledgements = ref([]);
  const sortOptions = ref([]);
  const totals = ref({});
  const textFilter = ref('');

  const createModalVariant = ref('existing');
  const createModalPayload = ref(null);
  const createModalLoading = ref(false);

  const selectedEmployeeAcknowledgement = ref(null);
  const sendReminderLoading = ref(false);
  const cancelLoading = ref(false);
  const selectedEmployeeAcknowledgementIds = ref(new Set());

  const bulkCreateModalResults = ref(null);
  const bulkCreateModalLoading = ref(false);
  const bulkSendReminderModalResults = ref(null);
  const bulkReminderLoading = ref(false);

  const previewData = ref(null);
  const previewLoading = ref(false);

  const compareModalOpen = ref(false);
  const compareOldProfileUlid = ref(null);
  const compareOldVersionUlid = ref(null);
  const compareNewProfileUlid = ref(null);
  const compareNewVersionUlid = ref(null);
  const compareAlertType = ref(null);
  const compareSignedAt = ref(null);

  const csvDownloadLoading = ref(false);

  const ACKNOWLEDGEMENT_ALERT_OPTIONS = [
    { id: 'pending_acknowledgement', label: ACKNOWLEDGEMENT_ALERT_LABELS.pending_acknowledgement },
    { id: 'no_acknowledgement', label: ACKNOWLEDGEMENT_ALERT_LABELS.no_acknowledgement },
    { id: 'job_profile_id_changed', label: ACKNOWLEDGEMENT_ALERT_LABELS.job_profile_id_changed },
    { id: 'job_profile_content_changed', label: ACKNOWLEDGEMENT_ALERT_LABELS.job_profile_content_changed },
    { id: 'acknowledgement_out_of_date', label: ACKNOWLEDGEMENT_ALERT_LABELS.acknowledgement_out_of_date },
    { id: 'acknowledgement_ok', label: ACKNOWLEDGEMENT_ALERT_LABELS.acknowledgement_ok },
  ];

  const selectedAcknowledgementStatuses = ref([]);

  // Used to apply active filter on first visit
  const showIsActiveDefault = ref(true);

  const createModalEmployeeName = computed(() => createModalPayload.value?.employeeName || '');
  const createModalEmployeeEmail = computed(() => createModalPayload.value?.employeeEmail || '');
  const createModalJobProfile = computed(() => createModalPayload.value?.jobProfile || '');

  const selectedEmployeesForBulkCreate = computed(() => {
    const ids = selectedEmployeeAcknowledgementIds.value;

    if (!ids || !ids.size) return [];

    return employeeAcknowledgements.value.filter((employee) => ids.has(employee.ulid));
  });

  const setTextFilter = (value) => {
    textFilter.value = value;
  };

  const resetIsActiveDefault = () => {
    showIsActiveDefault.value = true;
  };

  const updateSortOptions = (newSortOptions) => {
    sortOptions.value = newSortOptions;
  };

  const loadEmployeeAcknowledgements = async ({
    routeQuery,
    selectedFilters,
    page,
    searchTerm: newSearchTerm,
  }) => {
    setLoading({ pageLoading: true, listLoading: true });

    try {
      let params = routeQuery || {};

      if (!params.sort && !selectedSubMenuOption.value) {
        params.sort = 'first_name';
      } else if (selectedSubMenuOption.value) {
        params = { ...params, sort: selectedSubMenuOption.value };
      }

      params.page = page || undefined;

      const parameters = {
        params,
        searchTerm: newSearchTerm,
        selectedFilters,
      };

      const {
        items = [], pagination, sorting, textFilter: responseTextFilter,
      } = await getEmployeeAcknowledgements(parameters);

      employeeAcknowledgements.value = items;

      totals.value = {
        ...totals.value,
        active: pagination?.active || 0,
        inactive: pagination?.inactive || 0,
      };

      updateSortOptions((sorting?.options || []).map(({ id }) => id));
      useAppPagination().updatePagination(pagination);

      if (responseTextFilter?.value !== undefined) {
        setTextFilter(responseTextFilter.value);
      }
    } catch (err) {
      addErrorSnackbar({
        text: `Employees load error: ${err.message || err}`,
      });

      throw err;
    } finally {
      setLoading({ pageLoading: false, listLoading: false });
    }
  };

  const downloadEmployeeAcknowledgementsCsv = async (routeQuery) => {
    csvDownloadLoading.value = true;

    try {
      let params = routeQuery || {};

      if (!params.sort && !selectedSubMenuOption.value) {
        params.sort = 'first_name';
      } else if (selectedSubMenuOption.value) {
        params = { ...params, sort: selectedSubMenuOption.value };
      }

      const commaSeparatedString = await downloadEmployeeAcknowledgementsCsvRequest({
        params,
        selectedFilters: mergedTempFilters.value,
        searchTerm: textFilter.value,
      });

      const { projectName } = authData.value;
      const today = format(new Date(), READABLE_FILE_NAME_FORMAT);

      const fileTitle = `${kebabCase(projectName.toLowerCase())}-employee-acknowledgements-${today}.csv`;

      handleCSVDownload({
        commaSeparatedString,
        autoClick: true,
        title: fileTitle,
      });
    } catch (error) {
      addErrorSnackbar({ text: `Employee acknowledgements CSV download: ${error.message}` });

      throw error;
    } finally {
      csvDownloadLoading.value = false;
    }
  };

  const loadTotals = async ({ selectedFilters, routeQuery } = {}) => {
    try {
      const {
        acknowledgement_alert: _,
        ...filtersWithoutAlert
      } = selectedFilters || {};

      const {
        acknowledgement_alert: __,
        ...routeQueryWithoutAlert
      } = routeQuery || {};

      const summary = await getEmployeeAcknowledgementSummary({
        selectedFilters: filtersWithoutAlert,
        params: routeQueryWithoutAlert,
      });

      const {
        active: _summaryActive,
        inactive: _summaryInactive,
        ...summaryRest
      } = summary || {};

      totals.value = {
        ...totals.value,
        ...summaryRest,
      };
    } catch (err) {
      addErrorSnackbar({
        text: `Employees totals load error: ${err.message || err}`,
      });
    }
  };

  const loadPreview = async (employeeUlid) => {
    if (!employeeUlid) return;

    previewLoading.value = true;

    try {
      previewData.value = await getEmployeeAcknowledgementPreview(employeeUlid);
    } catch (e) {
      addErrorSnackbar({ text: `Load error - ${e.message}` });

      previewData.value = null;
    } finally {
      previewLoading.value = false;
    }
  };

  const clearPreview = () => {
    previewData.value = null;
    previewLoading.value = false;
  };

  const openCompareModal = ({
    oldProfileUlid,
    oldVersionUlid,
    newProfileUlid,
    newVersionUlid,
    alertType,
    signedAt,
  }) => {
    compareOldProfileUlid.value = oldProfileUlid;
    compareOldVersionUlid.value = oldVersionUlid;
    compareNewProfileUlid.value = newProfileUlid;
    compareNewVersionUlid.value = newVersionUlid;
    compareAlertType.value = alertType || null;
    compareSignedAt.value = signedAt || null;
    compareModalOpen.value = true;
  };

  const closeCompareModal = () => {
    compareModalOpen.value = false;
    compareOldProfileUlid.value = null;
    compareOldVersionUlid.value = null;
    compareNewProfileUlid.value = null;
    compareNewVersionUlid.value = null;
    compareAlertType.value = null;
    compareSignedAt.value = null;
  };

  const loadPage = async (params) => {
    const resolvedParams = { ...params };

    if (showIsActiveDefault.value) {
      const { routeQuery = {} } = resolvedParams;

      resolvedParams.routeQuery = isEmpty(routeQuery)
        ? { is_active: 'active' }
        : { ...routeQuery, is_active: routeQuery.is_active || 'active' };

      showIsActiveDefault.value = false;
    }

    loadEmployeeAcknowledgements(resolvedParams);
    loadTotals(resolvedParams);
    loadFilters(resolvedParams);

    if (resolvedParams?.routeQuery?.type === 'employee' && resolvedParams?.routeQuery?.preview) {
      loadPreview(resolvedParams?.routeQuery?.preview);
    }
  };

  const updateSelectedAcknowledgementStatuses = (optionIds) => {
    selectedAcknowledgementStatuses.value = optionIds;
  };

  const clearSelectedEmployees = () => {
    selectedEmployeeAcknowledgementIds.value = new Set();
  };

  const setSelectedEmployees = (ulids) => {
    selectedEmployeeAcknowledgementIds.value = new Set(ulids);
  };

  const toggleSelectedEmployee = (ulid, selected) => {
    const next = new Set(selectedEmployeeAcknowledgementIds.value);

    if (selected) next.add(ulid);
    else next.delete(ulid);

    selectedEmployeeAcknowledgementIds.value = next;
  };

  const resetSelectedAcknowledgementStatus = (router) => {
    if (!selectedAcknowledgementStatuses.value?.length) return;

    selectedAcknowledgementStatuses.value = [];

    const fieldsToUse = fields.value || [];
    const newValue = [];
    const existingField = fieldsToUse.find((field) => field.id === 'acknowledgement_alert');

    const newField = existingField
      ? { ...existingField, value: newValue }
      : {
        id: 'acknowledgement_alert',
        label: 'Acknowledgement Status',
        type: 'flatlist',
        async: false,
        asyncUrl: null,
        position: 'column_1',
        alwaysOn: false,
        options: ACKNOWLEDGEMENT_ALERT_OPTIONS,
        category: 'basic',
        value: newValue,
      };

    const newFields = existingField
      ? fieldsToUse.map((field) => (field.id === 'acknowledgement_alert' ? newField : field))
      : [...fieldsToUse, newField];

    updateFilters({ filters: newFields });
    sendFilterRequest({ router });
  };

  const selectAcknowledgementStatus = (optionIds, router) => {
    const currentAcknowledgementAlertIds = [...selectedAcknowledgementStatuses.value].sort();
    const targetAcknowledgementAlertIds = [...optionIds].sort();
    const fieldsToUse = fields.value || [];

    const isSameSelection = currentAcknowledgementAlertIds.length === targetAcknowledgementAlertIds.length
      && currentAcknowledgementAlertIds.every((id, index) => id === targetAcknowledgementAlertIds[index]);

    // Clicking the already-selected card clears the filter, otherwise apply the card's option ids.
    const newIds = isSameSelection ? [] : optionIds;

    selectedAcknowledgementStatuses.value = newIds;

    // Clear the search text filter
    setTextFilter('');

    // Get filter objects from optionIds
    const newValue = newIds
      .map((id) => ACKNOWLEDGEMENT_ALERT_OPTIONS.find((option) => option.id === id))
      .filter(Boolean);

    // Use existing field if it exists, otherwise create a new one to maintain card filter functionality
    const existingField = fieldsToUse.find((field) => field.id === 'acknowledgement_alert');
    const newField = existingField
      ? { ...existingField, value: newValue }
      : {
        id: 'acknowledgement_alert',
        label: 'Acknowledgement Status',
        type: 'flatlist',
        async: false,
        asyncUrl: null,
        position: 'column_1',
        alwaysOn: false,
        options: ACKNOWLEDGEMENT_ALERT_OPTIONS,
        category: 'basic',
        value: newValue,
      };

    const newFields = existingField
      ? fieldsToUse.map((field) => (field.id === 'acknowledgement_alert' ? newField : field))
      : [...fieldsToUse, newField];

    updateFilters({ filters: newFields });
    sendFilterRequest({ router });
  };

  const openCreateModal = (payload, variant = 'existing') => {
    createModalPayload.value = normalizeCreateModalPayload(payload);
    createModalVariant.value = variant;
    setModal({ field: 'employeeAcknowledgementCreate', value: true });
  };

  const closeCreateModal = () => {
    setModal({ field: 'employeeAcknowledgementCreate', value: false });
    createModalPayload.value = null;
  };

  const submitCreateAcknowledgement = async (refreshParams) => {
    const payload = createModalPayload.value;

    if (!payload?.employeeUlid) return;

    createModalLoading.value = true;
    try {
      const response = await createEmployeeAcknowledgement({
        employeeUlids: [payload.employeeUlid],
      });

      const result = response?.results?.[0];
      const status = result?.status;

      if (status === 'success' || status === 'email_failed') {
        const employeeName = createModalEmployeeName.value || 'this employee';

        addSuccessSnackbar({
          text: `An acknowledgement request has been emailed to ${employeeName}.`,
        });

        closeCreateModal();

        if (refreshParams) loadPage(refreshParams);
      } else if (status === 'skipped_pending_status') {
        addErrorSnackbar({ text: 'This employee already has a pending acknowledgement. Please refresh the page.' });
      } else if (status === 'skipped_no_profile') {
        addErrorSnackbar({ text: 'This employee does not have a job profile on file.' });
      } else if (status === 'no_email') {
        addErrorSnackbar({ text: 'This employee does not have an e-mail address on file.' });
      } else {
        addErrorSnackbar({ text: 'Failed to create acknowledgement' });
      }
    } catch (err) {
      addErrorSnackbar({
        text: err?.message || 'Failed to create acknowledgement',
      });
    } finally {
      createModalLoading.value = false;
    }
  };

  const openSendReminderModal = (payload) => {
    setModal({ field: 'employeeAcknowledgementSendReminder', value: true });

    selectedEmployeeAcknowledgement.value = payload;
  };

  const closeSendReminderModal = () => {
    setModal({ field: 'employeeAcknowledgementSendReminder', value: false });

    selectedEmployeeAcknowledgement.value = null;
  };

  const openBulkSendReminderModal = () => {
    setModal({ field: 'employeeAcknowledgementBulkSendReminder', value: true });

    bulkSendReminderModalResults.value = null;
  };

  const closeBulkSendReminderModal = () => {
    setModal({ field: 'employeeAcknowledgementBulkSendReminder', value: false });

    bulkSendReminderModalResults.value = null;
  };

  const openCancelModal = (payload) => {
    setModal({ field: 'employeeAcknowledgementCancel', value: true });

    selectedEmployeeAcknowledgement.value = payload;
  };

  const closeCancelModal = () => {
    setModal({ field: 'employeeAcknowledgementCancel', value: false });

    selectedEmployeeAcknowledgement.value = null;
  };

  const openBulkCreateModal = () => {
    setModal({ field: 'employeeAcknowledgementBulkCreate', value: true });

    bulkCreateModalResults.value = null;
    bulkCreateModalLoading.value = false;
  };

  const closeBulkCreateModal = () => {
    setModal({ field: 'employeeAcknowledgementBulkCreate', value: false });

    bulkCreateModalResults.value = null;
  };

  const submitBulkCreateAcknowledgement = async () => {
    const employees = selectedEmployeesForBulkCreate.value;

    if (!employees?.length) return;

    const employeeUlids = employees.map((employee) => employee.ulid).slice(0, 50);

    bulkCreateModalLoading.value = true;

    try {
      const response = await createEmployeeAcknowledgement({ employeeUlids });

      const { results = [] } = response || {};

      bulkCreateModalResults.value = results;
      bulkCreateModalLoading.value = false;

      const successCount = results.filter((result) => result.status === 'success').length;
      const emailFailedCount = results.filter((result) => result.status === 'email_failed').length;
      const skippedPending = results.filter((result) => result.status === 'skipped_pending_status').length;
      const skippedNoProfile = results.filter((result) => result.status === 'skipped_no_profile').length;
      const noEmailCount = results.filter((result) => result.status === 'no_email').length;

      if (successCount > 0) {
        addSuccessSnackbar({
          text: successCount === 1
            ? 'Acknowledgement created successfully'
            : `Acknowledgements created for ${successCount} employee${successCount === 1 ? '' : 's'}`,
        });
      }

      if (emailFailedCount > 0) {
        addErrorSnackbar({
          text: emailFailedCount === 1
            ? 'Acknowledgement created but notification email failed'
            : `${emailFailedCount} acknowledgement(s) created but notification email failed`,
        });
      }

      if (skippedPending + skippedNoProfile + noEmailCount > 0 && successCount === 0 && emailFailedCount === 0) {
        addErrorSnackbar({ text: 'Some employees could not receive acknowledgements. See details below.' });
      }
    } catch (err) {
      addErrorSnackbar({
        text: err?.message || 'Failed to create acknowledgements',
      });

      bulkCreateModalLoading.value = false;
    }
  };

  const submitSendReminder = async (refreshParams) => {
    const selected = selectedEmployeeAcknowledgement.value;

    if (!selected?.ulid) return;

    sendReminderLoading.value = true;
    try {
      const response = await sendEmployeeAcknowledgementReminder({
        employeeUlids: [selected.ulid],
      });

      const result = response?.results?.[0];
      const status = result?.status;

      const employeeName = [selected.first_name, selected.last_name].filter(Boolean).join(' ') || 'this employee';

      if (status === 'success' || status === 'email_failed') {
        addSuccessSnackbar({
          text: `The acknowledgement request has been resent to ${employeeName}.`,
        });

        closeSendReminderModal();

        if (refreshParams) loadPage(refreshParams);
      } else if (status === 'ack_not_found') {
        addErrorSnackbar({ text: 'This acknowledgement could not be found. Please refresh the page.' });
      } else if (status === 'ack_no_email') {
        addErrorSnackbar({ text: 'This employee does not have an e-mail address on file.' });
      } else {
        addErrorSnackbar({ text: 'Failed to send reminder' });
      }
    } catch (err) {
      addErrorSnackbar({
        text: err?.message || 'Failed to send reminder',
      });
    } finally {
      sendReminderLoading.value = false;
    }
  };

  const submitBulkSendReminder = async () => {
    const employees = selectedEmployeesForBulkCreate.value;

    if (!employees?.length) return;

    const employeeUlids = employees.map((e) => e.ulid).slice(0, 50);

    bulkReminderLoading.value = true;

    try {
      const response = await sendEmployeeAcknowledgementReminder({ employeeUlids });

      const { results = [] } = response || {};

      bulkSendReminderModalResults.value = results;
      bulkReminderLoading.value = false;

      const successCount = results.filter((result) => result.status === 'success'
        || result.status === 'email_failed').length;
      const ackNotFoundCount = results.filter((result) => result.status === 'ack_not_found').length;
      const noEmailCount = results.filter((result) => result.status === 'ack_no_email').length;

      if (successCount > 0) {
        addSuccessSnackbar({
          text: successCount === 1
            ? 'Reminder sent successfully'
            : `Reminders sent to ${successCount} employee${successCount === 1 ? '' : 's'}`,
        });
      }

      if (ackNotFoundCount + noEmailCount > 0 && successCount === 0) {
        addErrorSnackbar({ text: 'Some employees could not receive reminders. See details below.' });
      }
    } catch (err) {
      addErrorSnackbar({
        text: err?.message || 'Failed to send reminders',
      });

      bulkReminderLoading.value = false;
    }
  };

  const submitCancel = async (refreshParams) => {
    const selected = selectedEmployeeAcknowledgement.value;

    if (!selected?.ulid) return;

    cancelLoading.value = true;

    try {
      const response = await cancelEmployeeAcknowledgement({
        employeeUlids: [selected.ulid],
      });

      const result = response?.results?.[0];
      const status = result?.status;

      if (status === 'success') {
        addSuccessSnackbar({ text: 'This acknowledgement request has been cancelled.' });

        closeCancelModal();

        if (refreshParams) loadPage(refreshParams);
      } else if (status === 'error_signed') {
        addErrorSnackbar({ text: 'This acknowledgement has already been signed. Please refresh the page.' });
      } else if (status === 'error_not_found' || status === 'error_no_pending') {
        addErrorSnackbar({ text: 'This acknowledgement could not be found. Please refresh the page.' });
      } else {
        addErrorSnackbar({ text: 'Failed to cancel acknowledgement' });
      }
    } catch (err) {
      addErrorSnackbar({
        text: err?.message || 'Failed to cancel acknowledgement',
      });
    } finally {
      cancelLoading.value = false;
    }
  };

  return {
    employeeAcknowledgements,
    sortOptions,
    totals,
    textFilter,
    createModalLoading,
    selectedEmployeeAcknowledgement,
    sendReminderLoading,
    cancelLoading,
    selectedEmployeeAcknowledgementIds,
    bulkCreateModalResults,
    bulkCreateModalLoading,
    bulkSendReminderModalResults,
    bulkReminderLoading,
    selectedEmployeesForBulkCreate,
    previewData,
    previewLoading,

    compareModalOpen,
    compareOldProfileUlid,
    compareOldVersionUlid,
    compareNewProfileUlid,
    compareNewVersionUlid,
    compareAlertType,
    compareSignedAt,

    csvDownloadLoading,

    updateSelectedAcknowledgementStatuses,
    selectedAcknowledgementStatuses,
    createModalEmployeeName,
    createModalEmployeeEmail,
    createModalJobProfile,

    setTextFilter,
    loadPage,
    resetIsActiveDefault,
    downloadEmployeeAcknowledgementsCsv,
    loadPreview,
    clearPreview,
    openCompareModal,
    closeCompareModal,
    selectAcknowledgementStatus,
    resetSelectedAcknowledgementStatus,
    clearSelectedEmployees,
    setSelectedEmployees,
    toggleSelectedEmployee,
    openCreateModal,
    closeCreateModal,
    submitCreateAcknowledgement,
    openSendReminderModal,
    closeSendReminderModal,
    openBulkSendReminderModal,
    closeBulkSendReminderModal,
    openCancelModal,
    closeCancelModal,
    openBulkCreateModal,
    closeBulkCreateModal,
    submitBulkCreateAcknowledgement,
    submitBulkSendReminder,
    submitSendReminder,
    submitCancel,
  };
});
